export type CalKillerFilters = {
  bookingKind?: string;
  dateEnd?: string;
  dateStart?: string;
  eventTypeId?: string;
  eventTypeSlug?: string;
  organizerId?: string;
  organizationId?: string;
  scheduledEnd?: string;
  scheduledStart?: string;
  schedulingSource?: string;
  status?: string;
};

export type CalKillerSummary = {
  activeBookings: number;
  blankEventTypeSlugBookings: number;
  cancelledBookings: number;
  customEventTypeBookings: number;
  legacyEventTypeBookings: number;
  earliestCreatedAt: string | null;
  futureActiveBookings: number;
  latestCreatedAt: string | null;
  latestUpdatedAt: string | null;
  missingCarespaceBookings: number;
  missingEncounterBookings: number;
  noShowBookings: number;
  totalBookings: number;
  unrecognizedOrBlankEventTypeSlugBookings: number;
};

export type CalKillerTrendPoint = {
  activeBookings: number;
  cancelledBookings: number;
  date: string;
  noShowBookings: number;
  totalBookings: number;
};

export type CalKillerClassRow = {
  bookingCount: number;
  completedCount: number;
  eventTypeClass: "canonical" | "custom" | "legacy" | "blank_or_unknown";
  futureCount: number;
  futureSinceCount: number;
};

export type CalKillerEventTypeRow = {
  activeCount: number;
  bookingCount: number;
  cancelledCount: number;
  eventTypeClass: "canonical" | "custom" | "legacy" | "blank_or_unknown";
  eventTypeId: number | null;
  completedCount: number;
  eventTypeIdCount: number;
  futureCount: number;
  recentCreatedCount: number;
  eventTypeLabel: string;
  eventTypeSlug: string | null;
  noShowCount: number;
  newestCreatedAt: string | null;
  oldestAgeDays: number;
  oldestCreatedAt: string | null;
};

export type CalKillerBookingKindRow = {
  activeCount: number;
  bookingCount: number;
  bookingKind: string | null;
  cancelledCount: number;
  label: string;
  noShowCount: number;
};

export type CalKillerOrganizationRow = {
  activeCount: number;
  bookingCount: number;
  cancelledCount: number;
  noShowCount: number;
  organizationId: string | null;
  organizationName: string;
};

export type CalKillerOrganizerRow = {
  activeCount: number;
  bookingCount: number;
  cancelledCount: number;
  noShowCount: number;
  organizerId: number | null;
  organizerName: string;
};

export type CalKillerSchedulingSourceRow = {
  activeCount: number;
  bookingCount: number;
  cancelledCount: number;
  label: string;
  noShowCount: number;
  schedulingSource: string | null;
};

export type CalKillerResponse = {
  accessBoundary: string;
  bookingKinds: CalKillerBookingKindRow[];
  classes: CalKillerClassRow[];
  eventTypes: CalKillerEventTypeRow[];
  recentCreatedSince: string;
  filters: CalKillerFilters;
  generatedAt: string;
  latestDataUpdatedAt: string | null;
  organizations: CalKillerOrganizationRow[];
  organizers: CalKillerOrganizerRow[];
  owner: string;
  refreshIntervalMinutes: number;
  schedulingSources: CalKillerSchedulingSourceRow[];
  summary: CalKillerSummary;
  timezone: string;
  trend: CalKillerTrendPoint[];
};

const CAL_KILLER_FILTER_KEYS = new Set([
  "bookingKind",
  "dateEnd",
  "dateStart",
  "eventTypeId",
  "eventTypeSlug",
  "organizerId",
  "organizationId",
  "scheduledEnd",
  "scheduledStart",
  "schedulingSource",
  "status",
]);

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function isFiniteNumber(value: unknown): value is number {
  return typeof value === "number" && Number.isFinite(value);
}

function isNullableNumber(value: unknown): value is number | null {
  return value === null || isFiniteNumber(value);
}

function isNullableString(value: unknown): value is string | null {
  return value === null || typeof value === "string";
}

function isFilters(value: unknown): value is CalKillerFilters {
  if (!isRecord(value)) return false;
  return Object.keys(value).every(
    (key) => CAL_KILLER_FILTER_KEYS.has(key) && typeof value[key] === "string",
  );
}

function isSummary(value: unknown): value is CalKillerSummary {
  if (!isRecord(value)) return false;
  const row = value as CalKillerSummary;
  return (
    isFiniteNumber(row.totalBookings) &&
    isFiniteNumber(row.activeBookings) &&
    isFiniteNumber(row.cancelledBookings) &&
    isFiniteNumber(row.noShowBookings) &&
    isFiniteNumber(row.futureActiveBookings) &&
    isFiniteNumber(row.missingCarespaceBookings) &&
    isFiniteNumber(row.missingEncounterBookings) &&
    isFiniteNumber(row.blankEventTypeSlugBookings) &&
    isFiniteNumber(row.customEventTypeBookings) &&
    isFiniteNumber(row.legacyEventTypeBookings) &&
    isFiniteNumber(row.unrecognizedOrBlankEventTypeSlugBookings) &&
    isNullableString(row.earliestCreatedAt) &&
    isNullableString(row.latestCreatedAt) &&
    isNullableString(row.latestUpdatedAt)
  );
}

function isTrendPoint(value: unknown): value is CalKillerTrendPoint {
  if (!isRecord(value)) return false;
  const row = value as CalKillerTrendPoint;
  return (
    typeof row.date === "string" &&
    isFiniteNumber(row.totalBookings) &&
    isFiniteNumber(row.activeBookings) &&
    isFiniteNumber(row.cancelledBookings) &&
    isFiniteNumber(row.noShowBookings)
  );
}

function isClassRow(value: unknown): value is CalKillerClassRow {
  if (!isRecord(value)) return false;
  const row = value as CalKillerClassRow;
  return (
    isFiniteNumber(row.bookingCount) &&
    isFiniteNumber(row.completedCount) &&
    isFiniteNumber(row.futureCount) &&
    isFiniteNumber(row.futureSinceCount) &&
    (row.eventTypeClass === "canonical" ||
      row.eventTypeClass === "custom" ||
      row.eventTypeClass === "legacy" ||
      row.eventTypeClass === "blank_or_unknown")
  );
}

function isEventTypeRow(value: unknown): value is CalKillerEventTypeRow {
  if (!isRecord(value)) return false;
  const row = value as CalKillerEventTypeRow;
  return (
    isFiniteNumber(row.bookingCount) &&
    isFiniteNumber(row.activeCount) &&
    isFiniteNumber(row.cancelledCount) &&
    isFiniteNumber(row.noShowCount) &&
    isFiniteNumber(row.oldestAgeDays) &&
    isNullableNumber(row.eventTypeId) &&
    typeof row.eventTypeIdCount === "number" &&
    isFiniteNumber(row.completedCount) &&
    isFiniteNumber(row.futureCount) &&
    isFiniteNumber(row.recentCreatedCount) &&
    typeof row.eventTypeLabel === "string" &&
    isNullableString(row.eventTypeSlug) &&
    isNullableString(row.newestCreatedAt) &&
    isNullableString(row.oldestCreatedAt) &&
    (row.eventTypeClass === "canonical" ||
      row.eventTypeClass === "custom" ||
      row.eventTypeClass === "legacy" ||
      row.eventTypeClass === "blank_or_unknown")
  );
}

function isBookingKindRow(value: unknown): value is CalKillerBookingKindRow {
  if (!isRecord(value)) return false;
  const row = value as CalKillerBookingKindRow;
  return (
    isFiniteNumber(row.bookingCount) &&
    isFiniteNumber(row.activeCount) &&
    isFiniteNumber(row.cancelledCount) &&
    isFiniteNumber(row.noShowCount) &&
    isNullableString(row.bookingKind) &&
    typeof row.label === "string"
  );
}

function isOrganizationRow(value: unknown): value is CalKillerOrganizationRow {
  if (!isRecord(value)) return false;
  const row = value as CalKillerOrganizationRow;
  return (
    isFiniteNumber(row.bookingCount) &&
    isFiniteNumber(row.activeCount) &&
    isFiniteNumber(row.cancelledCount) &&
    isFiniteNumber(row.noShowCount) &&
    isNullableString(row.organizationId) &&
    typeof row.organizationName === "string"
  );
}

function isOrganizerRow(value: unknown): value is CalKillerOrganizerRow {
  if (!isRecord(value)) return false;
  const row = value as CalKillerOrganizerRow;
  return (
    isFiniteNumber(row.bookingCount) &&
    isFiniteNumber(row.activeCount) &&
    isFiniteNumber(row.cancelledCount) &&
    isFiniteNumber(row.noShowCount) &&
    isNullableNumber(row.organizerId) &&
    typeof row.organizerName === "string"
  );
}

function isSchedulingSourceRow(value: unknown): value is CalKillerSchedulingSourceRow {
  if (!isRecord(value)) return false;
  const row = value as CalKillerSchedulingSourceRow;
  return (
    isFiniteNumber(row.bookingCount) &&
    isFiniteNumber(row.activeCount) &&
    isFiniteNumber(row.cancelledCount) &&
    isFiniteNumber(row.noShowCount) &&
    typeof row.label === "string" &&
    isNullableString(row.schedulingSource)
  );
}

export function isCalKillerResponse(value: unknown): value is CalKillerResponse {
  if (!isRecord(value)) return false;
  const body = value as Partial<CalKillerResponse>;
  return (
    typeof body.accessBoundary === "string" &&
    typeof body.generatedAt === "string" &&
    isNullableString(body.latestDataUpdatedAt) &&
    typeof body.owner === "string" &&
    typeof body.recentCreatedSince === "string" &&
    isFiniteNumber(body.refreshIntervalMinutes) &&
    typeof body.timezone === "string" &&
    isFilters(body.filters) &&
    isSummary(body.summary) &&
    Array.isArray(body.trend) &&
    body.trend.every(isTrendPoint) &&
    Array.isArray(body.eventTypes) &&
    body.eventTypes.every(isEventTypeRow) &&
    Array.isArray(body.classes) &&
    body.classes.every(isClassRow) &&
    Array.isArray(body.bookingKinds) &&
    body.bookingKinds.every(isBookingKindRow) &&
    Array.isArray(body.organizations) &&
    body.organizations.every(isOrganizationRow) &&
    Array.isArray(body.organizers) &&
    body.organizers.every(isOrganizerRow) &&
    Array.isArray(body.schedulingSources) &&
    body.schedulingSources.every(isSchedulingSourceRow)
  );
}
